import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Snackbar,
  useMediaQuery,
} from "@mui/material";
import { HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { brand, radius, shadows as ds, typeScale, surfaceTokens } from "./config/theme";

import AppSidebar from "./components/layout/AppSidebar";
import PageHeader from "./components/layout/PageHeader";
import DashboardPage from "./pages/DashboardPage";
import MyPlayersPage from "./pages/MyPlayersPage";
import MyTeamsPage from "./pages/MyTeamsPage";
import SchedulePage from "./pages/SchedulePage";
import AdpTrendsPage from "./pages/AdpTrendsPage";
import InsightsPage from "./pages/InsightsPage";
import WrappedPage from "./pages/WrappedPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { DraftDataProvider, useDraftData } from "./context/DraftDataContext";
import { AdpProvider } from "./context/AdpContext";
import { UIStateProvider, useUIState } from "./context/UIStateContext";

function buildTheme(darkMode: boolean) {
  const primary = darkMode ? brand.primary.dark : brand.primary.light;
  const secondary = darkMode ? brand.secondary.dark : brand.secondary.light;
  const c = surfaceTokens(darkMode);

  return createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: { main: primary, light: c.brandLight },
      secondary: { main: secondary },
      background: darkMode
        ? { default: brand.surface.dark, paper: brand.paper.dark }
        : { default: brand.surface.light, paper: brand.paper.light },
      success: { main: c.good },   // positive delta
      error:   { main: c.bad },    // NEGATIVE numbers only
      warning: { main: c.warn },
      info:    { main: "#3B82F6" },
      divider: c.border,
      text:    { primary: c.text, secondary: c.textLight },
    },
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: darkMode ? brand.sidebar.dark : brand.sidebar.light,
            color: darkMode ? "#e0e0e0" : secondary,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: primary,
            color: "#fff",
            "&:hover": {
              backgroundColor: darkMode ? c.accentLine : "#142611",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          elevation1: {
            boxShadow: ds.card,
          },
          elevation2: {
            boxShadow: ds.cardHover,
          },
          elevation3: {
            boxShadow: ds.dropdown,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: ds.card,
            borderRadius: radius.md,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontVariantNumeric: "tabular-nums",
          },
        },
      },
    },
    typography: {
      fontFamily: "Inter, Roboto, Arial, sans-serif",
      // Map design tokens onto MUI variant slots
      h1: { ...typeScale.display },
      h2: { ...typeScale.heading },
      h3: { ...typeScale.subheading },
      body1: { ...typeScale.body },
      body2: { ...typeScale.bodyStrong },
      caption: { ...typeScale.caption },
      h4: { fontWeight: 600 },
      h6: { ...typeScale.subheading },
      subtitle1: { ...typeScale.bodyStrong },
    },
    shape: {
      borderRadius: radius.md,
    },
  });
}

// ── Height sync (parent iframe postMessage) ───────────────────────

function useHeightSync() {
  useEffect(() => {
    let lastHeight = 0;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    function sendHeight() {
      const newHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      if (newHeight !== lastHeight) {
        window.parent.postMessage(
          { type: "setHeight", height: newHeight },
          "https://rosteros.com"
        );
        lastHeight = newHeight;
      }
    }

    function debouncedSendHeight() {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(sendHeight, 100);
    }

    sendHeight();
    window.addEventListener("resize", debouncedSendHeight);
    const observer = new MutationObserver(debouncedSendHeight);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => {
      window.removeEventListener("resize", debouncedSendHeight);
      observer.disconnect();
      if (timeout) clearTimeout(timeout);
    };
  }, []);
}

// ── Skip-summary toast ───────────────────────────────────────────

function SkipSummaryToast() {
  const { skipSummary, setSkipSummary } = useDraftData();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  useEffect(() => {
    if (skipSummary) {
      setToastMsg(skipSummary);
      setToastOpen(true);
      setSkipSummary("");
    }
  }, [skipSummary, setSkipSummary]);

  return (
    <Snackbar
      open={toastOpen}
      autoHideDuration={7000}
      onClose={(_, reason) => { if (reason !== "clickaway") setToastOpen(false); }}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert onClose={() => setToastOpen(false)} severity="warning" sx={{ width: "100%" }}>
        {toastMsg}
      </Alert>
    </Snackbar>
  );
}

// ── Inner app (has access to all contexts) ────────────────────────

const MOBILE_TABS = [
  { path: "/dashboard", label: "Home", icon: <DashboardOutlinedIcon /> },
  { path: "/players",   label: "Players", icon: <PeopleOutlinedIcon /> },
  { path: "/teams",     label: "Teams", icon: <GroupsOutlinedIcon /> },
  { path: "/adp-trends",label: "ADP", icon: <TrendingUpIcon /> },
  { path: "/schedule",  label: "Schedule", icon: <CalendarMonthOutlinedIcon /> },
];

function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const primary = muiTheme.palette.primary.main;
  const activeIdx = MOBILE_TABS.findIndex(t => location.pathname === t.path);

  return (
    <Paper
      elevation={4}
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <BottomNavigation
        value={activeIdx}
        onChange={(_, newIdx) => navigate(MOBILE_TABS[newIdx].path)}
        sx={{ bgcolor: "background.paper" }}
      >
        {MOBILE_TABS.map(({ label, icon }) => (
          <BottomNavigationAction
            key={label}
            label={label}
            icon={icon}
            sx={{
              minWidth: 0,
              fontSize: 10,
              "&.Mui-selected": { color: primary },
              "& .MuiBottomNavigationAction-label": { fontSize: "0.65rem" },
            }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
}

// ── Processing overlay ────────────────────────────────────────────

function ProcessingOverlay() {
  const { isProcessingCombos } = useDraftData();
  if (!isProcessingCombos) return null;
  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 3,
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: 8,
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            border: "3px solid",
            borderColor: "divider",
            borderTopColor: "primary.main",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            "@keyframes spin": { to: { transform: "rotate(360deg)" } },
            flexShrink: 0,
          }}
        />
        <Box sx={{ fontWeight: 600, fontSize: "0.82rem", color: "text.primary", whiteSpace: "nowrap" }}>
          Processing combos…
        </Box>
      </Box>
    </Box>
  );
}

const PAGE_ORDER = ["/dashboard", "/players", "/teams", "/adp-trends", "/schedule"];
const SWIPE_THRESHOLD_PX = 80;
const MAX_VERTICAL_SWIPE_PX = 56;

function hasHorizontalScrollParent(target: Element): boolean {
  let current: Element | null = target;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    if (
      (style.overflowX === "auto" || style.overflowX === "scroll")
      && current.scrollWidth > current.clientWidth + 1
    ) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

function isSwipeExcludedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('[data-no-swipe="true"]')) return true;
  if (
    target.closest(
      "input, textarea, select, button, a, [role='button'], [role='slider'], [role='tab'], [role='switch'], .MuiSlider-root, .MuiTableContainer-root, .MuiAutocomplete-root"
    )
  ) {
    return true;
  }
  return hasHorizontalScrollParent(target);
}

function AppInner() {
  useHeightSync();

  const muiTheme = useTheme();
  const { mobileDrawerOpen, setMobileDrawerOpen } = useUIState();
  const { rawData } = useDraftData();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const touchStart = useRef<{ x: number; y: number; target: EventTarget | null } | null>(null);
  const canSwipeBetweenPages = isMobile && rawData.length > 0 && !mobileDrawerOpen;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!canSwipeBetweenPages || e.touches.length !== 1) return;
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      target: e.target,
    };
  };

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!canSwipeBetweenPages || touchStart.current === null) return;

      const start = touchStart.current;
      touchStart.current = null;

      const deltaX = e.changedTouches[0].clientX - start.x;
      const deltaY = e.changedTouches[0].clientY - start.y;

      if (isSwipeExcludedTarget(start.target)) return;
      if (Math.abs(deltaY) > MAX_VERTICAL_SWIPE_PX) return;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
      if (Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

      const currentIdx = PAGE_ORDER.indexOf(location.pathname);
      if (currentIdx === -1) return;

      if (deltaX < 0 && currentIdx < PAGE_ORDER.length - 1) {
        navigate(PAGE_ORDER[currentIdx + 1]);
      } else if (deltaX > 0 && currentIdx > 0) {
        navigate(PAGE_ORDER[currentIdx - 1]);
      }
    },
    [canSwipeBetweenPages, location.pathname, navigate]
  );

  // Season Wrapped is a full-screen takeover — no sidebar/header/bottom-nav.
  // Rendering it standalone also collapses the in-flow document so the iframe
  // height-sync sizes to the Wrapped surface (see WrappedPage's sizer).
  if (location.pathname === "/wrapped") {
    return (
      <ErrorBoundary>
        <WrappedPage />
      </ErrorBoundary>
    );
  }

  const pageRoutes = (
    <Routes>
      <Route
        path="/dashboard"
        element={
          <ErrorBoundary>
            <DashboardPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/players"
        element={
          <ErrorBoundary>
            <MyPlayersPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/teams"
        element={
          <ErrorBoundary>
            <MyTeamsPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/schedule"
        element={
          <ErrorBoundary>
            <SchedulePage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/insights"
        element={
          <ErrorBoundary>
            <InsightsPage />
          </ErrorBoundary>
        }
      />
      {/* Old standalone routes now live as sections of /insights */}
      <Route path="/stacks" element={<Navigate to="/insights" replace />} />
      <Route path="/archetypes" element={<Navigate to="/insights" replace />} />
      <Route path="/leverage" element={<Navigate to="/insights" replace />} />
      <Route path="/charts" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/adp-trends"
        element={
          <ErrorBoundary>
            <AdpTrendsPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/billing/*"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );

  return (
    <Box
      sx={{ display: "flex", bgcolor: "background.default", minHeight: "100vh" }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <AppSidebar
        onNavigate={() => setMobileDrawerOpen(false)}
        isMobile={isMobile}
        mobileOpen={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      />

      {/* Content area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Page header + KPI strip */}
        <PageHeader onOpenMobileMenu={isMobile ? () => setMobileDrawerOpen(true) : undefined} />

        {/* Main page content */}
        <Box
          key={location.pathname}
          sx={{ flex: 1, overflow: "auto", animation: "fadeIn 0.2s ease-in",
            pb: isMobile ? "56px" : 0 }}
        >
          {pageRoutes}
        </Box>
      </Box>

      {isMobile && <MobileBottomNav />}
      <SkipSummaryToast />
      <ProcessingOverlay />
    </Box>
  );
}

// ── Root ──────────────────────────────────────────────────────────

function App() {
  const [darkMode, setDarkModeRaw] = useState<boolean>(() => {
    try { return localStorage.getItem("rosterOS_darkMode") === "true"; }
    catch { return false; }
  });

  const handleSetDarkMode = useCallback((v: boolean) => {
    setDarkModeRaw(v);
    try { localStorage.setItem("rosterOS_darkMode", String(v)); } catch {}
  }, []);

  const theme = useMemo(() => buildTheme(darkMode), [darkMode]);

  return (
    <ThemeProvider theme={theme}>
      <DraftDataProvider>
        <AdpProvider>
          <UIStateProvider darkMode={darkMode} setDarkMode={handleSetDarkMode}>
            <HashRouter>
              <AppInner />
            </HashRouter>
          </UIStateProvider>
        </AdpProvider>
      </DraftDataProvider>
    </ThemeProvider>
  );
}

export default App;
